import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Row, Col, Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { getAuthHeaders } from '../utils/authHelpers'; 

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;

function EditDealerModal({ show, handleClose, refreshDealers, dealerToEdit, setSubmissionMessage }) {
  const [formData, setFormData] = useState({
    dealerName: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: ''
    },
    shippingPreferences: ''
  });

  // Inline validation state
  const [errors, setErrors] = useState({
    dealerName: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    address: { street: '', city: '', state: '', zip: '', country: '' },
    shippingPreferences: ''
  });
  const [touched, setTouched] = useState({
    dealerName: false,
    contactPerson: false,
    contactEmail: false,
    contactPhone: false,
    address: { street: false, city: false, state: false, zip: false, country: false },
    shippingPreferences: false
  });
  const [formError, setFormError] = useState('');

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (dealerToEdit) {
      setFormData({
        dealerName: dealerToEdit.dealerName || '',
        contactPerson: dealerToEdit.contactPerson || '',
        contactEmail: dealerToEdit.contactEmail || '',
        contactPhone: dealerToEdit.contactPhone || '',
        address: {
          street: dealerToEdit.address?.street || '',
          city: dealerToEdit.address?.city || '',
          state: dealerToEdit.address?.state || '',
          zip: dealerToEdit.address?.zip || '',
          country: dealerToEdit.address?.country || ''
        },
        shippingPreferences: dealerToEdit.shippingPreferences || ''
      });
      // Reset validation state when opening
      setErrors({
        dealerName: '',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        address: { street: '', city: '', state: '', zip: '', country: '' },
        shippingPreferences: ''
      });
      setTouched({
        dealerName: false,
        contactPerson: false,
        contactEmail: false,
        contactPhone: false,
        address: { street: false, city: false, state: false, zip: false, country: false },
        shippingPreferences: false
      });
      setFormError('');
      // Clear parent messages when opening for edit
      setSubmissionMessage('', '');
    }
  }, [dealerToEdit, setSubmissionMessage]); // Added setSubmissionMessage to dependency array

  // ===== validators =====
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

  const validateField = (name, value) => {
    switch (name) {
      case 'dealerName':
        return value.trim() ? '' : 'Dealer Name is required.';
      case 'contactPerson':
        return value.trim() ? '' : 'Contact Person is required.';
      case 'contactEmail':
        if (!value.trim()) return 'Contact Email is required.';
        if (!emailRegex.test(value.trim())) return 'Please enter a valid email address.';
        return '';
      case 'contactPhone': {
        // Optional; if provided, require 7–15 digits after stripping non-digits
        const digits = value.replace(/\D/g, '');
        if (!value.trim()) return '';
        if (digits.length < 7 || digits.length > 15) return 'Please enter a valid phone number (7–15 digits).';
        return '';
      }
      case 'address.street':
      case 'address.city':
      case 'address.state':
      case 'address.country':
        return ''; // optional
      case 'address.zip': {
        const zip = value.trim();
        if (!zip) return '';
        if (!/^[A-Za-z0-9\- ]{3,10}$/.test(zip)) return 'Please enter a valid postal/zip code.';
        return '';
      }
      case 'shippingPreferences':
        return ''; // optional
      default:
        return '';
    }
  };

  const validateAll = (data) => {
    const nextErrors = {
      dealerName: validateField('dealerName', data.dealerName),
      contactPerson: validateField('contactPerson', data.contactPerson),
      contactEmail: validateField('contactEmail', data.contactEmail),
      contactPhone: validateField('contactPhone', data.contactPhone),
      address: {
        street: validateField('address.street', data.address.street),
        city: validateField('address.city', data.address.city),
        state: validateField('address.state', data.address.state),
        zip: validateField('address.zip', data.address.zip),
        country: validateField('address.country', data.address.country),
      },
      shippingPreferences: validateField('shippingPreferences', data.shippingPreferences)
    };

    const flatErrors = [
      nextErrors.dealerName,
      nextErrors.contactPerson,
      nextErrors.contactEmail,
      nextErrors.contactPhone,
      nextErrors.address.street,
      nextErrors.address.city,
      nextErrors.address.state,
      nextErrors.address.zip,
      nextErrors.address.country,
      nextErrors.shippingPreferences
    ].filter(Boolean);
    const firstError = flatErrors[0] || '';

    return { nextErrors, firstError };
  };

  const markAllTouched = () => {
    setTouched({
      dealerName: true,
      contactPerson: true,
      contactEmail: true,
      contactPhone: true,
      address: { street: true, city: true, state: true, zip: true, country: true },
      shippingPreferences: true
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
      // Validate this address field on change
      const errMsg = validateField(name, value);
      setErrors(prev => ({ ...prev, address: { ...prev.address, [addressField]: errMsg } }));
      setTouched(prev => ({ ...prev, address: { ...prev.address, [addressField]: true } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      const errMsg = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: errMsg }));
      setTouched(prev => ({ ...prev, [name]: true }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      const value = formData.address[addressField];
      const errMsg = validateField(name, value);
      setErrors(prev => ({ ...prev, address: { ...prev.address, [addressField]: errMsg } }));
      setTouched(prev => ({ ...prev, address: { ...prev.address, [addressField]: true } }));
    } else {
      const value = formData[name];
      const errMsg = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: errMsg }));
      setTouched(prev => ({ ...prev, [name]: true }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError('');
    setSubmissionMessage('', ''); // Clear any previous messages

    if (!dealerToEdit?._id) {
      setSubmissionMessage('Error: Dealer ID missing for update.', 'danger');
      setLoading(false);
      return;
    }

    // Validate all before sending
    const { nextErrors, firstError } = validateAll(formData);
    setErrors(nextErrors);
    markAllTouched();

    const hasErrors =
      firstError ||
      Object.values(nextErrors).some(v => {
        if (typeof v === 'string') return !!v;
        return Object.values(v).some(Boolean); // nested address
      });

    if (hasErrors) {
      setFormError(firstError || 'Please fix the highlighted fields.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_ROOT_URL}/dealers/${dealerToEdit.dealerId}`, {
        method: 'PUT',
        headers: getAuthHeaders(), // Use token from localStorage
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        refreshDealers(); // Success: refresh parent
        onHide(); // Close modal
      } else {
        setSubmissionMessage(result.message || 'Failed to update dealer.', 'danger');
      }
    } catch (error) {
      console.error('Error updating dealer:', error);
      setSubmissionMessage('Network error or server is unreachable.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const onHide = () => {
    setFormData({
      dealerName: '',
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
      address: { street: '', city: '', state: '', zip: '', country: '' },
      shippingPreferences: ''
    });
    setErrors({
      dealerName: '',
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
      address: { street: '', city: '', state: '', zip: '', country: '' },
      shippingPreferences: ''
    });
    setTouched({
      dealerName: false,
      contactPerson: false,
      contactEmail: false,
      contactPhone: false,
      address: { street: false, city: false, state: false, zip: false, country: false },
      shippingPreferences: false
    });
    setFormError('');
    handleClose();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Dealer: {dealerToEdit?.dealerName || ''} ({dealerToEdit?.dealerId || ''})</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {formError && <Alert variant="danger">{formError}</Alert>}

        <Form onSubmit={handleSubmit} noValidate>
          <h5 className="mb-3">Basic Information</h5>
          <Form.Group className="mb-3" controlId="editDealerName">
            <Form.Label>Dealer Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="dealerName"
              value={formData.dealerName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter dealer's company name"
              required
              isInvalid={touched.dealerName && !!errors.dealerName}
            />
            <Form.Control.Feedback type="invalid">{errors.dealerName}</Form.Control.Feedback>
          </Form.Group>

          <h5 className="mb-3 mt-4">Contact Information</h5>
          <Form.Group className="mb-3" controlId="editContactPerson">
            <Form.Label>Contact Person <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., John Doe"
              required
              isInvalid={touched.contactPerson && !!errors.contactPerson}
            />
            <Form.Control.Feedback type="invalid">{errors.contactPerson}</Form.Control.Feedback>
          </Form.Group>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="editContactEmail">
              <Form.Label>Contact Email <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., info@dealer.com"
                required
                pattern="^[^\s@]+@[^\s@]+\.[^\s@]{2,}$"
                title="Enter a valid email address (e.g., name@example.com)"
                isInvalid={touched.contactEmail && !!errors.contactEmail}
              />
              <Form.Control.Feedback type="invalid">{errors.contactEmail}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="editContactPhone">
              <Form.Label>Contact Phone</Form.Label>
              <Form.Control
                type="text"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., (123) 456-7890"
                inputMode="tel"
                isInvalid={touched.contactPhone && !!errors.contactPhone}
              />
              <Form.Text muted>Optional. Digits only; 7–15 digits.</Form.Text>
              <Form.Control.Feedback type="invalid">{errors.contactPhone}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <h5 className="mb-3 mt-4">Address</h5>
          <Form.Group className="mb-3" controlId="editAddressStreet">
            <Form.Label>Street Address</Form.Label>
            <Form.Control
              type="text"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., 123 Main St"
              isInvalid={touched.address.street && !!errors.address.street}
            />
            <Form.Control.Feedback type="invalid">{errors.address.street}</Form.Control.Feedback>
          </Form.Group>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="editAddressCity">
              <Form.Label>City</Form.Label>
              <Form.Control
                type="text"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., Anytown"
                isInvalid={touched.address.city && !!errors.address.city}
              />
              <Form.Control.Feedback type="invalid">{errors.address.city}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="editAddressState">
              <Form.Label>State</Form.Label>
              <Form.Control
                type="text"
                name="address.state"
                value={formData.address.state}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., CA"
                isInvalid={touched.address.state && !!errors.address.state}
              />
              <Form.Control.Feedback type="invalid">{errors.address.state}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="editAddressZip">
              <Form.Label>Zip Code</Form.Label>
              <Form.Control
                type="text"
                name="address.zip"
                value={formData.address.zip}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g., 90210"
                isInvalid={touched.address.zip && !!errors.address.zip}
              />
              <Form.Control.Feedback type="invalid">{errors.address.zip}</Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Form.Group className="mb-3" controlId="editAddressCountry">
            <Form.Label>Country</Form.Label>
            <Form.Control
              type="text"
              name="address.country"
              value={formData.address.country}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., USA"
              isInvalid={touched.address.country && !!errors.address.country}
            />
            <Form.Control.Feedback type="invalid">{errors.address.country}</Form.Control.Feedback>
          </Form.Group>

          <h5 className="mb-3 mt-4">Shipping Preferences</h5>
          <Form.Group className="mb-3" controlId="editShippingPreferences">
            <Form.Label>Shipping Preferences</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="shippingPreferences"
              value={formData.shippingPreferences}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., Prefer UPS, Account #12345"
              isInvalid={touched.shippingPreferences && !!errors.shippingPreferences}
            />
            <Form.Control.Feedback type="invalid">{errors.shippingPreferences}</Form.Control.Feedback>
          </Form.Group>

          <Button variant="primary" type="submit" disabled={loading} className="w-100 mt-4">
            {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Update Dealer'}
          </Button>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditDealerModal;
